import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Building2, Cog, Wrench, AlertTriangle, Clock, Package, Users, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [todayVisits, setTodayVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, incidentsRes, visitsRes] = await Promise.all([
        axios.get(`${API}/stats/admin`),
        axios.get(`${API}/incidents`),
        axios.get(`${API}/visits`)
      ]);
      setStats(statsRes.data);
      setRecentIncidents(incidentsRes.data.slice(0, 5));
      
      // Filter today's visits
      const today = new Date().toISOString().split('T')[0];
      setTodayVisits(visitsRes.data.filter(v => v.scheduled_date === today && v.status === 'programada'));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Empresas', value: stats?.companies_count || 0, icon: Building2, color: 'sky', link: '/admin/companies' },
    { label: 'Máquinas', value: stats?.machines_count || 0, icon: Cog, color: 'emerald', link: '/admin/machines' },
    { label: 'Trabajos', value: stats?.works_count || 0, icon: Wrench, color: 'violet', link: '/admin/works' },
    { label: 'Técnicos', value: stats?.technicians_count || 0, icon: Users, color: 'cyan', link: '/admin/technicians' },
    { label: 'Incidencias Pendientes', value: stats?.incidents_pending || 0, icon: AlertTriangle, color: 'amber', link: '/admin/incidents' },
    { label: 'Esperando Repuesto', value: stats?.machines_pending_parts || 0, icon: Package, color: 'rose', link: '/admin/machines' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      en_proceso: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      resuelto: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    const labels = { pendiente: 'Pendiente', en_proceso: 'En Proceso', resuelto: 'Resuelto' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-wider border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      baja: 'text-slate-400',
      media: 'text-amber-400',
      alta: 'text-orange-400',
      urgente: 'text-rose-400',
    };
    return <span className={`font-mono text-xs uppercase ${styles[priority]}`}>{priority}</span>;
  };

  return (
    <Layout title="Panel de Control">
      <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
        {/* Today's Visits Alert */}
        {todayVisits.length > 0 && (
          <Card className="bg-sky-500/5 border-sky-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-6 h-6 text-sky-400" />
                <div>
                  <p className="text-white font-medium">
                    {todayVisits.length} visita{todayVisits.length > 1 ? 's' : ''} programada{todayVisits.length > 1 ? 's' : ''} para hoy
                  </p>
                  <p className="text-sm text-slate-400">
                    {todayVisits.map(v => v.company_name).join(', ')}
                  </p>
                </div>
                <Link to="/admin/calendar" className="ml-auto text-sky-400 hover:text-sky-300 text-sm font-medium">
                  Ver calendario →
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link key={stat.label} to={stat.link}>
                <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                          {stat.label}
                        </p>
                        <p className="text-4xl font-rajdhani font-bold text-white">
                          {loading ? '-' : stat.value}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-sm bg-${stat.color}-500/10 flex items-center justify-center group-hover:bg-${stat.color}-500/20 transition-colors`}>
                        <Icon className={`w-6 h-6 text-${stat.color}-400`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent Incidents */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="font-rajdhani text-xl font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Últimas Incidencias
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : recentIncidents.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No hay incidencias</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {recentIncidents.map((incident) => (
                  <div key={incident.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getPriorityBadge(incident.priority)}
                          <span className="text-slate-600">•</span>
                          <span className="text-xs text-slate-500 font-mono">
                            {incident.company_name}
                          </span>
                        </div>
                        <h4 className="text-white font-medium truncate">{incident.title}</h4>
                        <p className="text-sm text-slate-400 truncate mt-1">{incident.description}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(incident.status)}
                        <span className="text-xs text-slate-500 font-mono">
                          {new Date(incident.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
